// Sugalarm — the universal glass back button (cycle 162; restyled
// cycle 308).
//
// Every back affordance in the SPA renders through this one component
// so "go back" looks and behaves identically everywhere: one smooth
// translucent capsule — blurred ~42% surface fill, an iridescent
// gradient border, and an inline accent-colored chevron followed by
// the parent-screen label — or, when no label fits, a 44px icon-only
// glass circle. Token-driven (the `--backbtn-*` / `--iri-border` sets
// in tokens.css) so it adapts across all themes and over photo
// backgrounds with no per-call styling.
//
// NO navigation logic lives here — the component only calls `onBack`;
// every call site passes its existing handler unchanged (replace-vs-
// push semantics, URL-param clears, slide transitions all stay owned
// by the caller). Don't hand-roll a one-off back link in a future
// cycle; use this.

import styles from "./BackButton.module.css";

interface Props {
  /** Parent-screen name ("Settings", "Log", "Calendar"). Omitted (or
   *  with `iconOnly`) → the icon-only glass circle. */
  label?: string;
  onBack: () => void;
  iconOnly?: boolean;
  /** Required when rendering icon-only (no visible text). */
  ariaLabel?: string;
  /** Photo-surface escape: opaque white label ink, applied only where
   *  the control sits over imagery. */
  onPhoto?: boolean;
  /** Cycle 169: a smaller pill (lower height, tighter padding, smaller
   *  chevron chip). Used by the mobile Settings drill-down header, which
   *  the full-size pill was crowding. Default false keeps every other
   *  call site byte-identical. */
  compact?: boolean;
  /** Cycle 183: render a SOLID filled CTA pill (reads --cta-fill/--cta-ink)
   *  instead of the frosted glass. Applied only on the Log + Calendar
   *  pages, where those tokens are locally overridden to purple — so the
   *  back control matches the purple buttons there. Keeps the 44px target,
   *  chevron + label, focus ring, active-scale, and the exact `onBack`
   *  contract. Default false → every other BackButton is byte-identical. */
  solid?: boolean;
  /** Cycle 203: render a GRADIENT CTA pill (reads --cta-grad for the fill,
   *  with a purple-tinted lift via the solid --cta-fill token for the
   *  shadow). Used on the Android-tablet View Log / Calendar screens to
   *  match the gradient View Log › button. Default false → byte-identical.
   *  Mutually exclusive with `solid` at the call site. */
  gradient?: boolean;
  /** Cycle 323: render a QUIET token-based nav pill (reads --bg-sunk /
   *  --line-strong, matching a neighbouring segmented toggle) so the
   *  accent purple is reserved for real actions. --accent appears only on
   *  press / keyboard focus, never at rest. Used by the Recent (24h) log
   *  screen's back controls. Default false → every other call site is
   *  byte-identical. Mutually exclusive with `solid` / `gradient`. */
  quiet?: boolean;
  /** Cycle 327: swap the default pill radius (999px) for the rounded-
   *  rectangle radius of the neighbouring `List | Calendar` segmented
   *  toggle (10px) so the two read as one system. Radius-only — composes
   *  with `quiet`. Scoped by the caller to `variant === "mobile"`; the
   *  shared component's default radius is unchanged, so kiosk/web/iPad
   *  back controls stay byte-identical. */
  squared?: boolean;
}

export function BackButton({
  label,
  onBack,
  iconOnly = false,
  ariaLabel,
  onPhoto = false,
  compact = false,
  solid = false,
  gradient = false,
  quiet = false,
  squared = false,
}: Props) {
  const iconMode = iconOnly || !label;
  if (import.meta.env.DEV && !label && !ariaLabel) {
    throw new Error(
      "BackButton: provide `label` or, for icon-only, an `ariaLabel`.",
    );
  }
  return (
    <button
      type="button"
      className={`${styles.btn} ${iconMode ? styles.iconOnly : ""} ${
        onPhoto ? styles.onPhoto : ""
      } ${compact ? styles.compact : ""} ${solid ? styles.solid : ""} ${
        gradient ? styles.gradient : ""
      } ${quiet ? styles.quiet : ""} ${squared ? styles.squared : ""}`}
      aria-label={iconMode ? (ariaLabel ?? label ?? "Back") : undefined}
      onClick={onBack}
    >
      <span className={styles.chev} aria-hidden />
      {!iconMode ? <span className={styles.label}>{label}</span> : null}
    </button>
  );
}
